import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { CookieService } from '../services/cookie.service';
import { MsgBannerService } from 'src/app/shared/components/msg-banner/msg-banner.service';
import { environment } from 'src/environments/environment';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { AuthResponse } from 'src/app/shared/models/AD/auth-response.model';

const TOKEN_HEADER_KEY = 'Authorization';

@Injectable({
	providedIn: 'root',
})
export class AppInterceptor implements HttpInterceptor {
	requestsInProcess = 0;
	isRefreshing = false;

	private tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

	constructor(
		private readonly spinner: NgxSpinnerService,
		private readonly authService: AuthService,
		private readonly router: Router,
		private readonly dialog: MatDialog,
		private readonly cookieService: CookieService,
		private readonly msgBanner: MsgBannerService
	) { }

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		let noSpinnerFlag = false;
		// Check for custom header to disable loading spinner
		if (req.headers.has(environment.noLoadingSpinnerHeader) || req.url.includes('json')) {
			noSpinnerFlag = true;
			req.headers.delete(environment.noLoadingSpinnerHeader);
		} else {
			this.requestsInProcess++;
			setTimeout(() => { this.spinner.show(); }, 0);
		}

		// Sets the token on the request headers
		const authData = this.cookieService.get('authResponse')
			? JSON.parse(this.cookieService.get('authResponse')) : null;

		if (authData && !req.url.includes('refresh-token')) {
			req = req.clone({ headers: req.headers.set(TOKEN_HEADER_KEY, `Bearer ${authData?.accessToken}`) });
		}

		return next.handle(req).pipe(
			map((event: HttpEvent<any>) => {
				return event;
			}),
			catchError((error: HttpErrorResponse) => {
				if (error.status == 403 && !req.url.includes('refresh-token')) {
					if (authData) {
						req = req.clone({ headers: req.headers.delete(TOKEN_HEADER_KEY) });
						return this.handle403Error(req, next, authData);
					} else {
						this.dialog.open(ConfirmDialogComponent, {
							maxWidth: '400px',
							disableClose: true,
							data: {
								message:
									`An authentication exception occurred. Please log in again.`,
								confirmationRequired: false,
							},
						});
						return throwError(() => new Error());
					}
				}
				return throwError(() => error);
			}),
			finalize(() => {
				if (!noSpinnerFlag) {
					this.requestsInProcess--;
					if (this.requestsInProcess === 0) {
						this.spinner.hide();
					}
				}
			})
		);
	}

	private handle403Error(request: HttpRequest<any>, next: HttpHandler, authData: AuthResponse) {
		if (!this.isRefreshing) {
			this.isRefreshing = true;
			this.tokenSubject.next(null);

			return this.authService
				.refreshToken({ refreshToken: authData?.refreshToken })
				.pipe(
					switchMap((res) => {
						this.isRefreshing = false;

						if (res?.accessToken) {
							authData.accessToken = res?.accessToken;
							this.cookieService.set('authResponse', JSON.stringify(authData));

							this.tokenSubject.next(res.accessToken);
							
							const retryReq = request.clone({
								headers: request.headers.set(TOKEN_HEADER_KEY, `Bearer ${res.accessToken}`)
							});
							return next.handle(retryReq);
						} else {
							this.handleSessionExpired();
							return throwError(() => new Error('Invalid refresh response'));
						}
					}),
					catchError((error) => {
						if (this.requestsInProcess > 0)
							this.requestsInProcess--;

						this.handleSessionExpired();

						return throwError(() => error);
					})
				);
		}

		return this.tokenSubject.pipe(
			filter((token) => token != null), // wait for new token
			take(1),
			switchMap((token) => {
				const retryReq = request.clone({
					headers: request.headers.set(TOKEN_HEADER_KEY, `Bearer ${token}`)
				});
				return next.handle(retryReq);
			})
		);
	}

	private handleSessionExpired(): void {
		this.isRefreshing = false;

		this.msgBanner.addMessage('Session expired. Please log in again.');
		this.spinner.hide();

		this.authService.logout();
	}
}